// hooks/useFirestoreCollection.js
import { useState, useEffect, useMemo } from 'react';
import { useFirestore } from './useFirestore';

export function useFirestoreCollection(collectionName, options = {}) {
  const {
    autoLoad = true,
    realTime = false,
    conditions = [],
    orderBy = null,
    orderDirection = 'asc'
  } = options;

  const {
    data,
    loading,
    error,
    getAll,
    create,
    update,
    remove,
    getById,
    query,
    subscribe,
    clearError,
    service
  } = useFirestore(collectionName);

  const [unsubscribe, setUnsubscribe] = useState(null);

  // Load data on mount
  useEffect(() => {
    if (autoLoad) {
      if (realTime) {
        // Set up real-time subscription
        const unsubscribeFn = subscribe(conditions, orderBy);
        setUnsubscribe(() => unsubscribeFn);
        
        return () => {
          if (unsubscribeFn) {
            unsubscribeFn();
          }
        };
      } else {
        // One-time load
        if (conditions.length > 0 || orderBy) {
          query(conditions, orderBy, orderDirection);
        } else {
          getAll();
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoLoad, realTime]); // Removed dependencies that change on every render
  
  // Cleanup subscription on unmount
  useEffect(() => {
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [unsubscribe]);

  // Memoized filtered/sorted data
  const processedData = useMemo(() => {
    if (!data) return [];
    return data;
  }, [data]);

  return {
    data: processedData,
    loading,
    error,
    create,
    update,
    remove,
    getById,
    query,
    clearError,
    service,
    refresh: () => conditions.length > 0 || orderBy ? query(conditions, orderBy, orderDirection) : getAll()
  };
}